/**
 * Cart Drawer
 *
 * Slide-out cart preview accessible from the header.
 * Shows cart items and a link to the full checkout page.
 */

import { useNavigate } from 'react-router-dom'
import { FiX, FiTrash2, FiShoppingCart } from 'react-icons/fi'
import { Button } from '../ui/button'
import { useCheckoutStore, selectCartItemCount } from '../../store/useCheckoutStore'

export function CartDrawer() {
  const navigate = useNavigate()
  const cart = useCheckoutStore((s) => s.cart)
  const isCartOpen = useCheckoutStore((s) => s.isCartOpen)
  const closeCart = useCheckoutStore((s) => s.closeCart)
  const removeFromCart = useCheckoutStore((s) => s.removeFromCart)
  const itemCount = useCheckoutStore(selectCartItemCount)

  if (!isCartOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 z-40" onClick={closeCart} />

      {/* Drawer */}
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-sm bg-background border-l border-border shadow-xl z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="font-semibold text-foreground flex items-center gap-2">
            <FiShoppingCart size={18} />
            Cart ({itemCount})
          </h3>
          <button
            onClick={closeCart}
            className="w-8 h-8 flex items-center justify-center rounded-[var(--radius-button)] hover:bg-background-secondary"
          >
            <FiX size={18} />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto p-4">
          {cart.items.length === 0 ? (
            <div className="text-center py-12">
              <FiShoppingCart size={36} className="mx-auto text-foreground-muted mb-3" />
              <p className="text-sm text-foreground-muted">Your cart is empty</p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {cart.items.map((item) => (
                <div key={item.id} className="flex items-center gap-3">
                  {item.imageUrl && (
                    <img
                      src={item.imageUrl}
                      alt={item.name}
                      className="w-12 h-12 rounded-[var(--radius-button)] object-cover shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{item.name}</p>
                    <p className="text-xs text-foreground-muted">
                      ${item.price.toFixed(2)} x {item.quantity}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-semibold text-foreground">
                      ${(item.price * item.quantity).toFixed(2)}
                    </p>
                    <button
                      className="text-xs text-red-500 hover:text-red-700"
                      onClick={() => removeFromCart(item.id)}
                    >
                      <FiTrash2 size={12} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {cart.items.length > 0 && (
          <div className="border-t border-border p-4 flex flex-col gap-3">
            <div className="flex justify-between font-semibold text-foreground">
              <span>Subtotal</span>
              <span>${cart.subtotal.toFixed(2)}</span>
            </div>
            <Button
              className="w-full"
              onClick={() => {
                closeCart()
                navigate('/checkout')
              }}
            >
              View Cart & Checkout
            </Button>
          </div>
        )}
      </div>
    </>
  )
}
